import {
  useLocation,
  useNavigate,
  useParams as useRouteParams,
} from 'react-router-dom';
import Editor from 'components/Editor';
import {useEffect, useMemo, useState} from 'react';
import {useMobile} from 'utils/hooks/useMobile';
import {fetchCompositionReviewStatusService} from 'store/services/compositionServices';
import useNewReviewed from 'features/useNewReviewed';
import {useAblyWebsocket} from 'utils/functions/createConnection';
import {
  insertMarkersIntoParagraphs,
  createParagraphsFromTextRaw,
  Marker,
} from 'utils/functions/editor/paragraphs';
import {
  CompetenceMarker,
  CompositionReviewedStatus,
} from 'utils/types/newReviewed';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import PageLoading from 'components/PageLoading';
import TourProvider from 'features/TourProvider/TourProvider';
import {useTour} from 'utils/hooks/useTour';
import {updateProfileService} from 'store/services/profileServices';
import {connect} from 'react-redux';
import {timerSetup} from 'utils/functions/timer';
import StructureDropdown from 'components/StructureSidebar/StructureDropdown';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {usePrevious, useQueryParam} from 'utils/hooks';
import ComparisonModal from 'containers/Evolution/components/ComparisonModal';
import {createUserFeelingsRequest} from 'store/reducers/userFeelings';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {updateHasSeenCompositionRequest} from 'APIs/letrusAPI/compositionReview';
import {Button, useModals} from 'letrus-ui';
import {useLongPoll} from 'utils/hooks/useLongPoll';
import {fetchZeroGradeComment} from 'APIs/letrusAPI/zeroGradeComment';
import {isFocusModeFeedbackReleased} from 'contexts/FocusMode';
import {useCloseAiNotice} from 'features/useCloseAiNotice';
import {useHasSeenAiNotice} from 'features/useHasSeenAiNotice';
import styles from './NewReviewed.module.scss';
import Highlight from './components/Highlight';
import GoalModal from './modals/GoalModal';
import ZeroGradeModal from './modals/ZeroGradeModal';
import CommentsLoadingModal from './modals/CommentsLoadingModal';
import IntroductionModalHighlights from './modals/IntroductionModalHighlights';
import AiNoticeModal from './modals/AiNoticeModal';
import Header from './components/Header';
import GeneralView from './views/GeneralView';
import TeacherRevisionPending from './components/TeacherRevisionPending';

export enum HighlightVariant {
  positivos = 'positive',
  atencao = 'attention',
  zeramento = 'zeroGrade',
}

enum CompetenceName {
  c1 = 'Competência 1',
  c2 = 'Competência 2',
  c3 = 'Competência 3',
  c4 = 'Competência 4',
  c5 = 'Competência 5',
  c6 = 'Competência 6',
}

enum IndicatorsName {
  palavras = 'words',
  paragrafos = 'paragraphs',
  pontos = 'dots',
  virgulas = 'commas',
  erros_ortograficos = 'orthographics_errors',
  conectivos = 'connectives',
  oralidades = 'oralities',
  sentencas = 'sentences',
  agentes_sociais = 'social_agents',
  propositivos = 'propositionals',
}

interface DispatchProps {
  createUserFeelingsRequest: typeof createUserFeelingsRequest;
}

const ZERO_GRADE_REASONS_IDS = [4, 8, 9, 18, 20, 21];

type NewReviewedProps = DispatchProps;

function NewReviewed({
  createUserFeelingsRequest,
}: NewReviewedProps): JSX.Element {
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const [failedToConnect, setFailedToConnect] = useState(false);
  const [competenceDetails, setCompetenceDetails] = useState({});
  const [selectedIndicator, setSelectedIndicator] = useState<string>();
  const [paragraphs, setParagraphs] = useState(new Map([['0', {start: 0}]]));
  const [compositionStatus, setCompositionStatus] =
    useState<CompositionReviewedStatus>();

  // Initialize tour state and persistence
  const tour = useTour({storageKey: 'hasClosedReviewTour'});

  const previousIsLoadingReview = usePrevious(isLoadingReview);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParam();
  const {composition_id, view} = useRouteParams();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';

  const modals = useModals([
    'goals',
    'zeroGrade',
    'loadingComments',
    'introHighlights',
    'comparisonModal',
    'aiNotice',
  ]);

  const updateHasSeenComposition = updateHasSeenCompositionRequest();

  const {data: zeroGradeCommentData} = fetchZeroGradeComment(
    {
      genre_id: compositionStatus?.genre?.id ?? 0,
      user_role: 'student',
    },
    {
      isPaused: () => view !== 'zeramento',
    },
  );

  const {trackEvent} = useTrackEvent();
  const {isMobile} = useMobile();

  const {navigateToLearningPath, learningPath} = useLearningPathNavigation(
    learningPathUserInstanceId,
  );

  const compositionStatusLongPoll = useLongPoll(
    getCompositionStatus,
    15 * 1000,
    10,
    () => setFailedToConnect(true),
  );

  const ablyWebsocket = useAblyWebsocket({
    onError: () => compositionStatusLongPoll.start(),
  });

  const review = useNewReviewed({
    httpResponse: compositionStatus,
    websocketMessages: ablyWebsocket.messages,
  });

  const seenByStudent = compositionStatus?.review?.seen_by_student;
  const hasAlreadyChosenNextGoal = !!compositionStatus?.review?.goal_competence;
  const {webSocketReviewId} = review.getWebSocketFinish();
  const isZeroGrade =
    compositionStatus?.review?.zero_grader?.is_zeroed ??
    review.getWebSocketZeroGrader();
  const isUserFeelingsOn = getFeatureFlag(
    'feedback_emocional_ao_ver_devolutiva',
  ).isOn;

  const indicatorsShownStats =
    review.getShownStats(compositionStatus?.genre?.shown_stats ?? []) ?? [];

  const {userProfile, userRole} = getUserData();

  // AI-usage notice, shown once per account (not per composition) before
  // either correction path (ZeroGradeModal / IntroductionModalHighlights) is
  // released — persisted in the backend (ai_notice_seen_at), independent of
  // ai_probability.
  const hasSeenAiNotice = useHasSeenAiNotice(userProfile);

  useCloseAiNotice(userProfile, modals.aiNotice);

  const tourSteps = useMemo(() => review.getTourSteps(), []);

  const hasParentId = !!compositionStatus?.parent?.id;
  const currentReviewId = compositionStatus?.review?.id ?? webSocketReviewId!;
  const parentReviewId = compositionStatus?.parent?.review_id;
  // Markers for TextHighlighter
  // Extract highlight markers from paragraphs
  // Each marker is enriched with the paragraph index to associate it with its respective paragraph
  // The resulting array contains objects with the start, end, and className of each marker,
  // along with the paragraph index where the marker is found.
  const highlights = useMemo(() => {
    return Array.from(paragraphs.entries()).flatMap(
      ([paragraphIndex, value]) => {
        const {markers = []} = value as {
          markers?: {start: number; end: number; className: string}[];
        };

        return markers.map(({start, end, className}) => ({
          start,
          end,
          className,
          paragraph: Number(paragraphIndex),
        }));
      },
    );
  }, [paragraphs]);

  useEffect(() => {
    const sessionTimer = timerSetup();
    getCompositionStatus();
    trackEvent('feedback_screen_view');

    return () => {
      trackEvent('feedback_session_duration', {
        composition: {
          composition_id: Number(composition_id),
        },
        time: {
          spent_time: sessionTimer.finish(),
        },
      });
    };
  }, []);

  useEffect(() => {
    if (isZeroGrade) {
      navigate({
        pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/zeramento`,
        search: location.search,
      });
      modals.loadingComments?.close();
      modals.introHighlights?.close();

      if (hasSeenAiNotice) {
        modals.zeroGrade?.showModal();
      } else if (!modals.aiNotice?.open) {
        modals.aiNotice?.showModal();
      }
      return;
    }

    const finishedLoadingReview = !isLoadingReview && previousIsLoadingReview;

    if ((view === 'apresentacao' || !view) && finishedLoadingReview) {
      if (compositionStatus?.status === 'reviewed' || seenByStudent) {
        if (hasSeenAiNotice) {
          proceedToReview();
        } else if (!modals.aiNotice?.open) {
          modals.aiNotice?.showModal();
        }
      } else {
        modals.loadingComments?.showModal();
      }
    }

    if (compositionStatus?.id && !compositionStatus?.flag_onboarding) {
      tour.openTour();
    }
  }, [
    view,
    compositionStatus,
    isZeroGrade,
    isLoadingReview,
    previousIsLoadingReview,
    hasSeenAiNotice,
  ]);

  useEffect(() => {
    if (
      (view === 'geral' || view === 'zeramento') &&
      compositionStatus?.review?.id &&
      !seenByStudent
    ) {
      updateHasSeenComposition.trigger({
        id: compositionStatus?.review?.id,
      });
    }
  }, [view, compositionStatus, webSocketReviewId]);

  useEffect(() => {
    if (view === 'atencao' || view === 'positivos' || view === 'zeramento') {
      getViewCompetenceAndParagraphs(view, compositionStatus);
    }
  }, [view, review.comments, compositionStatus]);

  function onClickHighlightMainButton() {
    if (view === 'positivos') {
      navigate({
        pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/atencao`,
        search: location.search,
      });
    }

    if (view === 'atencao') {
      navigate({
        pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/geral`,
        search: location.search,
      });
    }
  }

  function getHeaderInfo(view?: string) {
    if (view === 'geral') {
      return {
        title: 'Análise Completa',
        goBack: () =>
          navigate({
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/atencao`,
            search: location.search,
          }),
      };
    }

    if (view === 'atencao') {
      return {
        title: isMobile
          ? 'Pontos de Melhoria'
          : 'Análise de Pontos de Melhoria',
        goBack: () =>
          navigate({
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/positivos`,
            search: location.search,
          }),
      };
    }

    if (view === 'positivos') {
      return {
        title: isMobile ? 'Pontos Positivos' : 'Análise de Pontos Positivos',
        goBack: () => {
          navigate({
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva`,
            search: location.search,
          });
          modals.introHighlights?.showModal();
        },
      };
    }

    if (view === 'indicators') {
      return {
        title: 'Devolutiva Escrita',
        goBack: () =>
          navigate({
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/geral`,
            search: location.search,
          }),
      };
    }

    return {
      title: 'Devolutiva da Escrita',
      goBack: () => navigate(-1),
    };
  }

  function getCompetenceComment() {
    if (view === 'positivos') {
      return competenceDetails?.[0]?.comment?.mandou_bem;
    }

    if (view === 'atencao') {
      return competenceDetails?.[0]?.comment?.podemos_melhorar;
    }

    // TEMP: Concatenating zero grade comment explanation with general comment
    // Instead of showing it in markers tooltip as part of the MVP
    if (view === 'zeramento') {
      if (compositionStatus?.review?.annotations?.length) {
        const commentHtml = `${compositionStatus?.review?.annotations[0].comment} <br /><br />`;

        return (
          commentHtml + compositionStatus?.review?.zero_grade_reason?.comment
        );
      }

      return compositionStatus?.review?.zero_grade_reason?.comment ?? '';
    }

    return '';
  }

  function getViewCompetenceAndParagraphs(
    view: string,
    compositionReview?: CompositionReviewedStatus,
  ) {
    const highlightedMarkers: Marker[] = Object.values(
      review.textMarkers ?? {},
    ).reduce((acc: Marker[], current) => {
      current?.forEach((marker: CompetenceMarker, index) => {
        acc.push({
          id: index,
          competence_id: marker?.competence_id,
          start: marker.boundaries[0],
          end: marker.boundaries[1],
          paragraph: marker.paragraph,
          className: '',
        });
      });

      return acc;
    }, []);

    const visibleMarkers = {
      positivos: review.getGreaterMarkers(highlightedMarkers),
      atencao: review.getSmallerMarkers(highlightedMarkers),
    };

    const visibleCompetences = {
      positivos: review.getGreaterCompetence(visibleMarkers.positivos),
      atencao: review.getSmallerCompetence(visibleMarkers.atencao),
    };

    setCompetenceDetails(Object.values(visibleCompetences[view] ?? []));

    const paragraphs = insertMarkersIntoParagraphs(
      createParagraphsFromTextRaw(compositionReview?.composition_raw ?? ''),
      review.getEditorMarkers({
        markers:
          view === 'zeramento' ? review?.annotations : visibleMarkers[view],
        view,
      }),
    );

    setParagraphs(paragraphs ?? new Map());
  }

  async function getCompositionStatus() {
    try {
      // fetch the composition review status
      const {data} = await fetchCompositionReviewStatusService(composition_id);
      const {status, automatic_review} = data;

      setIsLoadingReview(false);
      setCompositionStatus(data);

      // open a websocket connection if the composition is not reviewed yet
      if (automatic_review && status !== 'reviewed') {
        ablyWebsocket.initialize(composition_id);
      } else {
        // if it is reviewed, use the endpoint data as reference
        compositionStatusLongPoll.stop();
      }

      return;
    } catch (error) {
      // TODO: deal with connection failure, confirm fallback error screen with design team.
    }
  }

  function createGeneralParagraphsMarkers(markers: Marker[]) {
    const generalParagraphs = insertMarkersIntoParagraphs(
      createParagraphsFromTextRaw(compositionStatus?.composition_raw ?? ''),
      review.getEditorMarkers({markers}),
    );

    setParagraphs(generalParagraphs ?? new Map());
  }

  function handleHeaderGoBack() {
    getHeaderInfo(view)?.goBack();
  }

  function proceedToReview() {
    if (isZeroGrade) {
      modals.zeroGrade?.showModal();
    } else {
      modals.introHighlights?.showModal();
    }
  }

  function handleExitButtonClick() {
    if (hasAlreadyChosenNextGoal || view === 'zeramento') {
      navigateToLearningPath.nextStep(Number(composition_id));
    } else {
      modals.goals?.showModal();
    }
  }

  function handleTourFinish() {
    updateProfileService({
      id: userProfile?.id,
      flag_onboarding: true,
    });
  }

  function getCompositionIndicator(indicatorName: string) {
    return review.indicators?.[IndicatorsName[indicatorName]] ?? 0;
  }

  // --- DEADLINE BLOCK ---
  // 1) teacher_revision_deadline exists
  // 2) current date is earlier than teacher_revision_deadline
  // 3) status indicates active review (teacher_revision_pending, automatic_review_running, etc.)
  // If released before the deadline, status changes to ‘reviewed’, unlocking the feedback

  // Deadline set by the teacher for the composition revision
  const teacherRevisionDeadlineString =
    compositionStatus?.test_teacher_revision_deadline;

  // Current date and time
  const currentDateTime = new Date();

  // Convert the teacher's revision deadline to a Date object if it exists
  const teacherRevisionDeadlineDate = teacherRevisionDeadlineString
    ? new Date(teacherRevisionDeadlineString)
    : null;

  // Revision statuses that indicate an active review
  const activeRevisionStatuses = [
    'teacher revision pending',
    'automatic review running',
    'manual review pending',
  ];

  // Check if the composition is currently in an active revision status
  const isRevisionCurrentlyActive = activeRevisionStatuses?.includes(
    compositionStatus?.status ?? '',
  );

  // Check if the current date is before the teacher's revision deadline
  const isBeforeTeacherRevisionDeadline =
    teacherRevisionDeadlineDate !== null &&
    currentDateTime < teacherRevisionDeadlineDate;

  const isBlockedFocusModeFeedback =
    learningPath?.uses_focus_mode && !isFocusModeFeedbackReleased(learningPath);

  const shouldShowTeacherRevisionPending =
    isBlockedFocusModeFeedback &&
    (learningPath?.status === 'finished' ||
      learningPath?.status === 'reviewed' ||
      learningPath?.status === 'expired');

  const shouldRedirectToCurrentStep =
    isBlockedFocusModeFeedback && !shouldShowTeacherRevisionPending;

  useEffect(() => {
    if (shouldRedirectToCurrentStep) {
      navigateToLearningPath.currentStep();
    }
  }, [shouldRedirectToCurrentStep]);

  // Determine if the TeacherRevisionPending component should be displayed
  const shouldBlockDueToActiveTeacherRevision =
    isRevisionCurrentlyActive && isBeforeTeacherRevisionDeadline;

  if (shouldBlockDueToActiveTeacherRevision) {
    return <TeacherRevisionPending />;
  }

  if (shouldShowTeacherRevisionPending) {
    return <TeacherRevisionPending />;
  }

  if (shouldRedirectToCurrentStep) {
    return <PageLoading />;
  }

  return (
    <>
      {isLoadingReview ? (
        <PageLoading />
      ) : (
        <main className={styles.container} data-focusid="container">
          <Header
            title={getHeaderInfo(view)?.title}
            onGoBack={() => handleHeaderGoBack()}
            onExit={() => handleExitButtonClick()}
            showExitButton={view === 'geral' || view === 'zeramento'}
            setShouldShowComparisonModal={() =>
              modals.comparisonModal?.showModal()
            }
            isRewrite={hasParentId}
            shouldShowComparisonButton={!!currentReviewId && !!parentReviewId}
            getReviewComparisonActivities={
              parentReviewId && currentReviewId
                ? () => modals.comparisonModal?.showModal()
                : undefined
            }
          />

          <div className={styles.wrapper}>
            {(view === 'positivos' ||
              view === 'atencao' ||
              (view === 'zeramento' && isZeroGrade)) && (
              <Highlight
                variant={HighlightVariant[view]}
                subtitle={
                  view === 'zeramento'
                    ? compositionStatus?.review?.zero_grade_reason?.title || ''
                    : `${
                        compositionStatus?.genre?.competences?.find(
                          (competence) =>
                            competence.id ===
                            competenceDetails?.[0]?.competence_id,
                        )?.nickname || ''
                      }: ${
                        CompetenceName[competenceDetails?.[0]?.competence] || ''
                      }`
                }
                comment={getCompetenceComment()}
                onClickMainButton={() => onClickHighlightMainButton()}
                indicator={competenceDetails?.[0]?.indicator}
                shouldShowReasonsButton={
                  view === 'zeramento' &&
                  ZERO_GRADE_REASONS_IDS?.includes(
                    compositionStatus?.review?.zero_grade_reason?.id ?? 0,
                  )
                }
                zeroGradeReasons={zeroGradeCommentData}
                genreName={compositionStatus?.genre?.name}
              />
            )}

            {view === 'geral' && (
              <GeneralView
                comments={review.comments}
                totalScore={review.totalScore}
                competences={compositionStatus?.genre?.competences}
                reviewGridName={compositionStatus?.genre?.review_grid_name}
                onMarkerChange={(markers) =>
                  createGeneralParagraphsMarkers(markers)
                }
                textMarkers={
                  compositionStatus?.review?.text_markers?.json_metadatas ??
                  review.textMarkers
                }
              />
            )}

            {view === 'indicadores' && (
              <div className={styles.structureDropdown}>
                {indicatorsShownStats.map((indicator) => (
                  <>
                    <StructureDropdown
                      key={indicator?.varname}
                      name={indicator?.public_name ?? ''}
                      statGoal={{
                        goal: indicator?.goal,
                        max: indicator?.ideal_range_max ?? 0,
                        min: indicator?.ideal_range_min ?? 0,
                      }}
                      comment={indicator?.description ?? ''}
                      indicatorName={indicator?.varname ?? ''}
                      selected={selectedIndicator === indicator?.varname}
                      value={getCompositionIndicator(indicator?.varname ?? '')}
                      onClickDropdown={() =>
                        setSelectedIndicator(indicator.varname)
                      }
                    />
                    <hr />
                  </>
                ))}
              </div>
            )}

            <aside className={styles.editorWrapper} data-focusid="editor">
              <Editor
                genre={compositionStatus?.genre?.name ?? ''}
                title={compositionStatus?.title ?? ''}
                newParagraphs={compositionStatus?.paragraphs ?? []}
                highlights={highlights}
                hasParentId={hasParentId}
                activityTitle={compositionStatus?.test_name ?? ''}
                view={view}
              />
            </aside>
          </div>

          {tour.isOpen && view === 'geral' && (
            <TourProvider
              isOpen
              steps={tourSteps}
              onRequestClose={() => {
                tour.closeTour();
                handleTourFinish();
              }}
              lastStepNextButton={
                <Button variant="primary">Ir para correção</Button>
              }
              showNumber
              rounded={16}
              accentColor="#ffbf00"
              disableInteraction
            />
          )}
        </main>
      )}

      <ComparisonModal
        id="comparisonModal"
        parentId={compositionStatus?.parent?.review_id}
        reviewId={compositionStatus?.review?.id}
      />

      <CommentsLoadingModal
        id="loadingComments"
        failedToConnect={failedToConnect}
        onClose={() => {
          modals.loadingComments?.close();

          if (hasSeenAiNotice) {
            proceedToReview();
          } else if (!modals.aiNotice?.open) {
            modals.aiNotice?.showModal();
          }
        }}
        indicators={review.indicators}
        shownStats={compositionStatus?.genre?.shown_stats ?? []}
        reviewId={compositionStatus?.review?.id ?? webSocketReviewId}
      />

      <AiNoticeModal
        id="aiNotice"
        onClose={() => {
          modals.aiNotice?.close();
          proceedToReview();
        }}
      />

      <ZeroGradeModal
        id="zeroGrade"
        onClose={() => modals.zeroGrade?.close()}
      />

      <IntroductionModalHighlights
        id="introHighlights"
        onClose={() => {
          modals.introHighlights?.close();
          navigate({
            pathname: `/atividades/escrita/${composition_id}/nova_devolutiva/positivos`,
            search: location.search,
          });
        }}
        genre={compositionStatus?.genre?.name ?? ''}
        externalReference={compositionStatus?.genre?.reference_value ?? 0}
        difference={review?.difference ?? 0}
        comparisonResult={review?.comparisonResult ?? ''}
      />

      <GoalModal
        id="goals"
        isUserFeelingsOn={isUserFeelingsOn}
        comments={review.comments}
        onFinish={() => navigateToLearningPath.nextStep(Number(composition_id))}
        reviewId={compositionStatus?.review?.id ?? webSocketReviewId!}
        competences={compositionStatus?.genre?.competences}
        createUserFeelingsRequest={createUserFeelingsRequest}
        networkId={Number(userRole?.school_network_id)}
        reviewGrid={compositionStatus?.genre?.review_grid ?? 0}
      />
    </>
  );
}

export default connect(null, {
  createUserFeelingsRequest,
})(NewReviewed);
